import React, { createContext, useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Folder } from "Models/folder";
import * as yup from "yup";

import { Order } from "../models/order";
import StorageBoxService from "../services/storage.box.service";
import { TaskStatus } from "../util/enums";
import { useAppState } from "./StateContext";

interface OrderContextProps {
  order?: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  hasTaskToPublish: boolean;
  handleUpdateOrder: (attribute: string, value: any) => void;
  changedOptions?: string[];
  setChangedOptions: React.Dispatch<React.SetStateAction<string[]>>;
  generalErrors: yup.ValidationError[];
  setGeneralErrors: React.Dispatch<React.SetStateAction<yup.ValidationError[]>>;
  refetchStorageBoxes: (response?: any, variables?: any) => void;
  isLoadingStorageBoxes: boolean;
  folders: Folder[];
  setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
}

const OrderContext = createContext<OrderContextProps>({} as OrderContextProps);

interface OrderProviderProps {
  children: React.ReactChild;
}

const OrderProvider = ({ children }: OrderProviderProps) => {
  const { currentUser } = useAppState();
  const [order, setOrder] = useState<Order>({} as Order);
  const [hasTaskToPublish, setHasTaskToPublish] = useState<boolean>(false);
  const [changedOptions, setChangedOptions] = useState<string[]>([]);
  const [generalErrors, setGeneralErrors] = useState<yup.ValidationError[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);

  useEffect(() => {
    if (order?.tasks?.length) {
      const task = order?.tasks?.find((task) => task.status === TaskStatus.WAITING_PUBLISH);

      setHasTaskToPublish(!!task);
    }
  }, [order?.tasks]);

  const handleUpdateOrder = (attribute: string, value: any) => {
    if (attribute && attribute !== "all") {
      if (!["string", "number", "boolean", "object"].includes(typeof value)) {
        setOrder((state: any) => ({ ...state, [attribute]: [...value] }));
      } else setOrder((state: any) => ({ ...state, [attribute]: value }));
    } else {
      setOrder({ ...value });
    }
  };

  const { mutate: refetchStorageBoxes, isLoading: isLoadingStorageBoxes } = useMutation(
    ["refetch.storage.boxes.of.order"],
    () =>
      StorageBoxService.getDefaultList({
        companyId: currentUser?.companyId,
        isActive: 1,
        sortBy: JSON.stringify([["updatedAt", "desc"]]),
        include: JSON.stringify([
          { association: "jobs", where: { id: order?.id } },
          { association: "storageStatus" },
          { association: "storageBoxType" },
          { association: "itemsJobs" },
          { association: "liftvanConfig" },
        ]),
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) {
          setOrder({
            ...order,
            storageBoxes: [
              ...(order?.storageBoxes?.filter((storageBox) => !storageBox.singleUse) || []),
              ...response.data,
            ].filter((item, index, self) => self.findIndex((t) => t.id === item.id) === index),
          });
        }
      },
    }
  );

  return (
    <OrderContext.Provider
      value={{
        hasTaskToPublish,
        order,
        setOrder,
        handleUpdateOrder,
        changedOptions,
        setChangedOptions,
        generalErrors,
        setGeneralErrors,
        refetchStorageBoxes,
        isLoadingStorageBoxes,
        folders,
        setFolders,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export { OrderContext, OrderProvider };
